/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */

import Heading from "components/ui/Heading";
import { Input, Select, Toggle } from "components/ui/Input";
import Label from "components/ui/Label";
import MyPhoneInput from "components/ui/MyPhoneInput";
import Stack from "components/ui/Stack";
import { ImageCropper } from "components/uploaders";
import useDevAttributes from "hooks/useDevAttributes";
import useFetcher from "hooks/useFetcher";
import { getRolesSelects } from "lib/v2/rolesApi";
import { useMemo } from "react";
import formatSelectOptions from "utils/formatSelectOptions";
import { verifyOnlyLetters } from "utils/validator";

const AdminInfo = ({
  image,
  setImage,
  setActive,
  isActive,
  adminRoleId,
  setAdminRoleId,
  formPermissions,
  setEmail,
  setPhoneNumber,
  setCountryCode,
  email,
  phoneNumber,
  countryCode,
  lastName,
  firstName,
  setLastName,
  setFirstName,
  setUsername,
}) => {
  const { data: roles, isLoading } = useFetcher(getRolesSelects, {
    dataKeyName: "roles",
  });

  const roleOptions = useMemo(
    () => formatSelectOptions(roles, null, true),
    [roles],
  );

  const { toggleAttr } = useDevAttributes({
    toggleAttr: `toggle-active-admin`,
  });

  return (
    <Stack className="" test-id="1-tdbsNy">
      <Heading as="h3" className="mb-3" test-id="9mjbpsPA">
        Personal Info
      </Heading>

      <Stack
        className="bg-secondary-accent space-y-8  p-[44px]"
        test-id="fyfwzaTQ"
      >
        <div className="w-fit mx-auto md:ml-0 md:mr-auto" test-id="ss7-Moxp">
          <ImageCropper
            onUpload={(url) => setImage(url)}
            providedImage={image}
            disabled={formPermissions.isDisabled}
            test-id="3Pz9daBD"
          />
        </div>

        <Stack test-id="Bu2UBQZm">
          <Label htmlFor="active_admin" size="large" test-id="6grC2rB0">
            Active Admin
          </Label>

          <div test-id="C_aNIV4i">
            <Toggle
              {...toggleAttr}
              id="active_admin"
              value={isActive}
              disabled={formPermissions.isDisabled}
              onChange={() => setActive(!isActive)}
              test-id="B4hq5NEQ"
            />
          </div>
        </Stack>

        <Stack test-id="x0WDMoQu">
          <Label
            htmlFor="admin_role"
            size="large"
            isRequired
            test-id="3aCobPMS"
          >
            Admin Role
          </Label>

          <Select
            id="admin_role"
            options={roleOptions}
            value={adminRoleId}
            onChange={(e) => setAdminRoleId(e.target.value)}
            isLoading={isLoading}
            disabled={formPermissions.isDisabled}
            test-id="ulxry29k"
          />
        </Stack>

        <div className="flex justify-between space-x-4" test-id="iBHDItqc">
          <Stack test-id="gXwmOt9d">
            <Label size="large" htmlFor="firstName" test-id="I2JVegj1">
              First Name
            </Label>

            <Input
              id="firstName"
              name="firstName"
              placeholder="John"
              disabled={formPermissions.isDisabled}
              onChange={({ target: { value } }) =>
                setFirstName(verifyOnlyLetters(value))
              }
              onBlur={(e) => {
                if (formPermissions.isCreating) {
                  const generatedUsername = `${verifyOnlyLetters(
                    e.target.value,
                  )}${lastName}`;

                  setUsername(
                    generatedUsername.toLowerCase().replace(/\s/g, ""),
                  );
                }
              }}
              value={firstName}
              test-id="Lf0poG3r"
            />
          </Stack>

          <Stack test-id="K0QYPYH0">
            <Label size="large" htmlFor="lastName" test-id="hpBjQwCG">
              Last Name
            </Label>

            <Input
              id="lastName"
              name="lastName"
              placeholder="Doe"
              disabled={formPermissions.isDisabled}
              onChange={({ target: { value } }) =>
                setLastName(verifyOnlyLetters(value))
              }
              onBlur={(e) => {
                if (formPermissions.isCreating) {
                  const generatedUsername = `${firstName}${verifyOnlyLetters(
                    e.target.value,
                  )}`;

                  setUsername(
                    generatedUsername.toLowerCase().replace(/\s/g, ""),
                  );
                }
              }}
              value={lastName}
              test-id="Bd1vwCLQ"
            />
          </Stack>
        </div>

        <Stack test-id="HESD7Xpd">
          <Label htmlFor="email" size="large" isRequired test-id="XusDIUTM">
            Email
          </Label>

          <Input
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={formPermissions.isDisabled}
            test-id="a0qnfyoQ"
          />
        </Stack>

        <Stack test-id="H9iCATzh">
          <Label
            htmlFor="user-contact"
            size="large"
            isRequired
            test-id="31dkszOA"
          >
            Contact No.
          </Label>

          <MyPhoneInput
            id="user-contact"
            countryCode={countryCode}
            setCountryCode={(code) => setCountryCode(code)}
            value={phoneNumber}
            onChange={(value) => setPhoneNumber(value)}
            disabled={formPermissions.isDisabled}
            testId="admins-admin-info-contact-no"
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default AdminInfo;
